/**
 * SunlightPresets — Built-in light-rig presets and sun-position helper.
 *
 * Extracted from SunlightManager.ts so that the preset registry can be read
 * independently (e.g. for UI dropdowns or tests) without constructing a full
 * Three.js scene.
 *
 * @see docs/ims-engine-api/11-feature-lighting-shadow.md
 */

import * as THREE from 'three';
import type { SunlightConfig } from '../api/types';

// ---------------------------------------------------------------------------
// Built-in presets
// ---------------------------------------------------------------------------

export const SUNLIGHT_PRESETS: Record<string, Required<SunlightConfig>> = {
  'studio-neutral': {
    elevation: 65,
    azimuth: 30,
    intensity: 0.5,
    color: '#ffffff',
    fillIntensity: 0, // HDRI provides IBL fill — directional fill causes a second specular dot in glass
    hemi: { sky: '#ffffff', ground: '#444444', intensity: 0.2 },
    castShadow: true,
  },
  'studio-soft': {
    elevation: 55,
    azimuth: 10,
    intensity: 0.3,
    color: '#ffffff',
    fillIntensity: 0, // HDRI provides IBL fill
    hemi: { sky: '#ffffff', ground: '#444444', intensity: 0.2 },
    castShadow: true,
  },
  daylight: {
    elevation: 50,
    azimuth: 200,
    intensity: 1.2,
    color: '#fff5e6',
    fillIntensity: 0, // HDRI IBL provides fill — directional fill adds second specular
    hemi: { sky: '#b0d8f5', ground: '#8b7355', intensity: 0.6 },
    castShadow: true,
  },
  'golden-hour': {
    elevation: 10,
    azimuth: 250,
    intensity: 0.8,
    color: '#FFBD91',
    fillIntensity: 0, // HDRI IBL provides fill
    hemi: { sky: '#ffd4a0', ground: '#7a5535', intensity: 0.5 },
    castShadow: true,
  },
  overcast: {
    elevation: 90,
    azimuth: 0,
    intensity: 0.3,
    color: '#d0dde8',
    fillIntensity: 0, // HDRI IBL provides fill
    hemi: { sky: '#c8d8e8', ground: '#707878', intensity: 0.9 },
    castShadow: false,
  },
  dramatic: {
    elevation: 30,
    azimuth: 160,
    intensity: 2.0,
    color: '#ffe8c0',
    fillIntensity: 0, // HDRI IBL provides fill
    hemi: { sky: '#2a3850', ground: '#302010', intensity: 0.3 },
    castShadow: true,
  },
  night: {
    elevation: 5,
    azimuth: 100,
    intensity: 0.05,
    color: '#a0b8d8',
    fillIntensity: 0, // HDRI IBL provides fill
    hemi: { sky: '#101820', ground: '#080808', intensity: 0.1 },
    castShadow: false,
  },
};

export const DEFAULT_SUNLIGHT_PRESET = 'studio-neutral';

// ---------------------------------------------------------------------------
// Sun-position helper
// ---------------------------------------------------------------------------

/**
 * Convert elevation/azimuth (degrees) to a Three.js world-space position vector.
 * The DirectionalLight.position is treated as the "from" direction — the light
 * shines toward the origin from this position.
 *
 * Returns a shared scratch vector — copy the result if you need to keep it.
 *
 * @param elevation - degrees above horizon (0° = horizon, 90° = overhead)
 * @param azimuth   - degrees clockwise from north (0° = +Z, 90° = +X, 180° = -Z, 270° = -X)
 * @param distance  - distance from origin (arbitrary — light direction only matters for dir-lights)
 */
const _scratchSunPos = new THREE.Vector3();
export function sunPosition(elevation: number, azimuth: number, distance = 10): THREE.Vector3 {
  const elRad = THREE.MathUtils.degToRad(elevation);
  const azRad = THREE.MathUtils.degToRad(azimuth);
  return _scratchSunPos.set(
    distance * Math.cos(elRad) * Math.sin(azRad),
    distance * Math.sin(elRad),
    distance * Math.cos(elRad) * Math.cos(azRad),
  );
}
